/**
 * The command → event mapping (Deliverable D, widened in Phase 2).
 *
 * Commands carry *intent*; events carry *fact*. `dispatch` translates one command
 * into zero or more events, appends them, and lets the pure reducer recompute
 * state. Keeping this mapping in one small, total, pure function makes the L3
 * surface auditable and is what the conformance tests pin down.
 *
 * Phase 1 (unchanged) — 1:1 to the §6.2 events:
 *   challenge   → constraint-suspended      activate    → constraint-activated
 *   declare     → constraint-declared       import-edge → edge-imported
 *   add-claim   → claim-added               promote     → profile-promoted
 *   record-lane → lane-status-recorded      raise-flag  → flag-raised
 *
 * Phase 2 (additive) — each still reduces to model/app events:
 *   reframe       → constraint-activated / -suspended  (frames are configs)
 *   compose-frame → constraint-activated (union of frames; enlarging)
 *   review        → lane-status-recorded { expert }
 *   flag-clear    → lane-status-recorded { community: 'none' }
 *   record-machine→ lane-status-recorded { machine: lane(evidence) }  (proved `lane`)
 *   register-pack → module-registered                  (additive; model records provenance)
 *   set-edge-modal→ edge-modal-set                      (@deprecated annotation)
 *
 * State-dependent commands are NOT here — the Store resolves them against the log:
 *   undo / redo       → move the history cursor (no events)
 *   apply-edge-modal  → profile-promoted with kernel.applyEdgeToProfile(kind, profile)
 */
import { lane } from '@progression/model';
/**
 * The pure command→event mapping. Only `PureCommand`s appear here — the
 * state-dependent commands (`undo`, `redo`, `apply-edge-modal`) are resolved by
 * the Store, which holds the log/state they need.
 */
export function commandToEvents(command) {
    switch (command.c) {
        // --- Phase 1 (1:1) ---
        case 'challenge':
            return [{ t: 'constraint-suspended', id: command.id }];
        case 'activate':
            return [{ t: 'constraint-activated', id: command.id }];
        case 'declare':
            return [{ t: 'constraint-declared', id: command.id, status: command.status, labelId: command.labelId }];
        case 'import-edge':
            return [{ t: 'edge-imported', edge: command.edge }];
        case 'add-claim':
            return [{ t: 'claim-added', claim: command.claim }];
        case 'promote':
            return [{ t: 'profile-promoted', claimId: command.claimId, to: command.to }];
        case 'record-lane':
            return [{ t: 'lane-status-recorded', systemClaimId: command.systemClaimId, lanes: command.lanes }];
        case 'raise-flag':
            return [{ t: 'flag-raised', systemClaimId: command.systemClaimId, noteId: command.noteId }];
        // --- Phase 2 framing ---
        case 'reframe': {
            const out = [];
            for (const id of command.frame.activate ?? [])
                out.push({ t: 'constraint-activated', id });
            for (const id of command.frame.suspend ?? [])
                out.push({ t: 'constraint-suspended', id });
            return out;
        }
        case 'compose-frame': {
            // Union of the frames' activations (enlarging; `reframe_enlarges`). Dedup so
            // replay is idempotent on overlapping frames.
            const seen = new Set();
            const out = [];
            for (const frame of command.frames) {
                for (const id of frame.activate ?? []) {
                    if (seen.has(id))
                        continue;
                    seen.add(id);
                    out.push({ t: 'constraint-activated', id });
                }
            }
            return out;
        }
        // --- Phase 2 lane transitions ---
        case 'review':
            return [{ t: 'lane-status-recorded', systemClaimId: command.systemClaimId, lanes: { expert: command.expert } }];
        case 'flag-clear':
            // The Community lane is ledger-driven; clearing one note maps to the model's
            // `flag-cleared` event (the lane only falls to `none` once all notes clear).
            return [{ t: 'flag-cleared', systemClaimId: command.systemClaimId, noteId: command.noteId }];
        case 'record-machine':
            // The Machine lane is assigned by the PROVED `lane(evidence)` function, so a
            // `conflict-proven` can only arise from a same-config refutation proof
            // (INV-LANE-2/3). The model reducer applies a second same-config guard.
            return [
                { t: 'lane-status-recorded', systemClaimId: command.systemClaimId, lanes: { machine: lane(command.evidence) } },
            ];
        // --- Phase 2 domain-pack registration ---
        case 'register-pack':
            // One `module-registered` event; the model adds ids additively
            // (INV-ADD-NOT-ALTER) and records per-module provenance.
            return [{ t: 'module-registered', registration: command.pack }];
        // --- Compute Integration: record an external computation as an evidence fact ---
        case 'record-computation':
            return [{ t: 'computation-recorded', record: command.record }];
        // --- modal-on-edges placeholder annotation (@deprecated; kept for back-compat) ---
        case 'set-edge-modal':
            return [{ t: 'edge-modal-set', edgeId: command.edgeId, m: command.m }];
        default: {
            // Exhaustive over PureCommand (undo/redo/apply-edge-modal are excluded and
            // handled by the Store).
            const _never = command;
            return _never;
        }
    }
}
//# sourceMappingURL=mapping.js.map